/**
 * M7 S8: the case (spec §2.4–§2.5, §8.3; break-it SL-7, EX-2, DS-2). Four links (`order`, `knowledge`, `coverup`,
 * `complicity`), scored over a bundle (`bundle.ts`):
 *
 * - per link `L = min(1, Σ entry weights in that link / linkCap)`;
 * - `evidenceWeight = meanScale × mean(L) + testimony − credibility`, clamped to 0..1 (fed to `proposeCharterVote`);
 * - a proof log weighs its own `weight` (originals 3, logs 1; the content tests hold them to `case.json weights`),
 *   any other kind `weights.texture` (0); an item the player holds only as a copy (`copy`, `withheld`) weighs
 *   × `weights.copyMult`; a repeatable instance weighs `corroboration.weight`; the case counts at most
 *   `meter.maxInCase` meter proofs;
 * - testimony: Okafor public or anonymous (one of them), Duarte; credibility: the stipend known to the hearing
 *   (any path after Blackmail), a sponsor whose pilot flies with the guardians; stacked, capped at `credibility.cap`.
 *   The stipend never adds a proof (DS-2): it is not a bundle entry and nothing here reads its amount.
 *
 * The **Case line** on the Logs screen (never a number) is the line of the best bundle the player could pick now
 * (`bestBundle`, greedy by marginal weight), so it matches what a hand-in can actually reach.
 *
 * Repeatable corroboration (§2.5): one instance per link at most once per `everyOnlineDays` online faction days and
 * at most `maxPerLink` per link, for the link's fee; the `knowledge` link's is the meter proof (`meter.ts`).
 *
 * Pure: Career-in, Career-out. Event-driven; nothing here runs per frame.
 */
import { withWallet } from '../career/ops'
import type { Career } from '../career/types'
import { availableEntries, bundleEntryKey, instanceCount, normalizeBundle, parseInstance, type CaseData } from './bundle'
import { hasLog } from './logs'
import type { CaseConfig, EvidenceLog } from './schemas'
import { hasFlag, onlineDaysOf, storyOf, withStory } from './state'
import { CASE_LINKS, type BundleEntry, type CaseLine, type CaseLink, type StoryItemId } from './types'

export type LinkScores = Record<CaseLink, number>

/** The hearing's view of the player beyond the bundle (testimony and credibility). */
export interface CaseContext {
  okaforPublic: boolean
  okaforAnonymous: boolean
  duarte: boolean
  /** The hearing learns of a Blackmail stipend (§8.3). */
  stipendKnown: boolean
  /** The sponsor's pilot has joined the guardians (§8.3, rev 3). */
  sponsorGuardian: boolean
}

/** The context the career implies: flags plus the finale paths in force or left. */
export function caseContextOf(career: Career): CaseContext {
  const finale = storyOf(career).finale
  const tookBlackmail = finale.paths.includes('blackmail') || finale.from === 'blackmail'
  return {
    okaforPublic: hasFlag(career, 'okafor_public'),
    okaforAnonymous: hasFlag(career, 'okafor_anonymous'),
    duarte: hasFlag(career, 'duarte_testifies'),
    stipendKnown: tookBlackmail || hasFlag(career, 'stipend_known'),
    sponsorGuardian: finale.paths.includes('guardians') || hasFlag(career, 'guardian_joined'),
  }
}

export function testimonyOf(ctx: CaseContext, cfg: CaseConfig): number {
  const okafor = ctx.okaforPublic ? cfg.testimony.okaforPublic : ctx.okaforAnonymous ? cfg.testimony.okaforAnonymous : 0
  return okafor + (ctx.duarte ? cfg.testimony.duarte : 0)
}

export function credibilityOf(ctx: CaseContext, cfg: CaseConfig): number {
  const raw = (ctx.stipendKnown ? cfg.credibility.stipendKnown : 0) + (ctx.sponsorGuardian ? cfg.credibility.sponsorGuardian : 0)
  return Math.min(cfg.credibility.cap, raw)
}

const itemLogsCache = new WeakMap<CaseData['logsById'], ReadonlyMap<string, readonly EvidenceLog[]>>()

function itemLogs(logs: CaseData['logsById'], item: StoryItemId): readonly EvidenceLog[] {
  let index = itemLogsCache.get(logs)
  if (index === undefined) {
    const built = new Map<string, EvidenceLog[]>()
    for (const log of logs.values()) {
      if (log.item === undefined) continue
      const list = built.get(log.item) ?? []
      list.push(log)
      built.set(log.item, list)
    }
    index = built
    itemLogsCache.set(logs, index)
  }
  return index.get(item) ?? []
}

function logWeight(log: EvidenceLog, cfg: CaseConfig): number {
  return log.kind === 'proof' ? log.weight : cfg.weights.texture
}

interface Contribution {
  link: CaseLink
  weight: number
  meter: boolean
}

/** What one entry adds to each link for this career (nothing when the player doesn't have it). */
function contributions(career: Career, entry: BundleEntry, data: CaseData): Contribution[] {
  const cfg = data.case
  if (entry.kind === 'item') {
    const item = storyOf(career).items.find((i) => i.id === entry.id)
    if (item === undefined) return []
    const mult = item.state === 'held' ? 1 : item.state === 'copy' || item.state === 'withheld' ? cfg.weights.copyMult : 0
    if (mult === 0) return []
    const out: Contribution[] = []
    for (const log of itemLogs(data.logsById, item.id)) {
      if (log.link === 'none') continue
      out.push({ link: log.link, weight: logWeight(log, cfg) * mult, meter: false })
    }
    return out
  }
  const log = data.logsById.get(entry.id)
  if (log !== undefined) {
    if (log.item !== undefined) return contributions(career, { kind: 'item', id: log.item }, data)
    if (log.repeatable || log.link === 'none') return []
    return hasLog(career, log.id) ? [{ link: log.link, weight: logWeight(log, cfg), meter: false }] : []
  }
  const inst = parseInstance(entry.id, cfg)
  if (inst === null || inst.n > instanceCount(career, inst.link)) return []
  return [{ link: inst.link, weight: cfg.corroboration.weight, meter: inst.link === 'knowledge' }]
}

/** Raw weight per link (before `linkCap`), meter proofs capped at `meter.maxInCase`. */
function linkSums(career: Career, entries: readonly BundleEntry[], data: CaseData): LinkScores {
  const sums: LinkScores = { order: 0, knowledge: 0, coverup: 0, complicity: 0 }
  let meters = 0
  for (const entry of normalizeBundle(entries, data)) {
    for (const c of contributions(career, entry, data)) {
      if (c.meter) {
        if (meters >= data.case.meter.maxInCase) continue
        meters++
      }
      sums[c.link] += c.weight
    }
  }
  return sums
}

/** Per-link `L` (0..1) for a bundle. */
export function linkScores(career: Career, entries: readonly BundleEntry[], data: CaseData): LinkScores {
  const sums = linkSums(career, entries, data)
  const cap = data.case.linkCap
  return {
    order: Math.min(1, sums.order / cap),
    knowledge: Math.min(1, sums.knowledge / cap),
    coverup: Math.min(1, sums.coverup / cap),
    complicity: Math.min(1, sums.complicity / cap),
  }
}

function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x
}

/** `evidenceWeight` (0..1) of a bundle: the number `proposeCharterVote` carries. */
export function evidenceWeight(
  career: Career,
  entries: readonly BundleEntry[],
  data: CaseData,
  ctx: CaseContext = caseContextOf(career),
): number {
  const l = linkScores(career, entries, data)
  const mean = (l.order + l.knowledge + l.coverup + l.complicity) / CASE_LINKS.length
  return clamp01(data.case.meanScale * mean + testimonyOf(ctx, data.case) - credibilityOf(ctx, data.case))
}

/** The Case line for a weight (thresholds in `case.json caseLine`). */
export function caseLineOf(weight: number, cfg: CaseConfig): CaseLine {
  if (weight >= cfg.caseLine.damning) return 'damning'
  if (weight >= cfg.caseLine.documented) return 'documented'
  if (weight >= cfg.caseLine.circumstantial) return 'circumstantial'
  return 'rumour'
}

/** Links the bundle doesn't touch: the opposition names them ("the Settlement file was never produced"). */
export function missingLinks(career: Career, entries: readonly BundleEntry[], data: CaseData): CaseLink[] {
  const l = linkScores(career, entries, data)
  return CASE_LINKS.filter((link) => l[link] <= 0)
}

/**
 * The strongest bundle the player could pick now (greedy: each step adds the entry with the largest gain in
 * capped link weight; ties keep `availableEntries` order). Up to `bundleMax` entries; entries that add nothing
 * are left out.
 */
export function bestBundle(career: Career, data: CaseData): BundleEntry[] {
  const pool = availableEntries(career, data)
  const cap = data.case.linkCap
  const capped = (s: LinkScores): number =>
    Math.min(cap, s.order) + Math.min(cap, s.knowledge) + Math.min(cap, s.coverup) + Math.min(cap, s.complicity)
  let picked: BundleEntry[] = []
  let score = 0
  const used = new Set<string>()
  while (picked.length < data.case.bundleMax) {
    let best: BundleEntry | undefined
    let bestScore = score
    for (const e of pool) {
      const key = bundleEntryKey(e, data.logsById)
      if (used.has(key)) continue
      const s = capped(linkSums(career, [...picked, e], data))
      if (s > bestScore + 1e-9) {
        best = e
        bestScore = s
      }
    }
    if (best === undefined) break
    picked = [...picked, best]
    used.add(bundleEntryKey(best, data.logsById))
    score = bestScore
  }
  return picked
}

export interface CaseStrength {
  weight: number
  line: CaseLine
  bundle: BundleEntry[]
}

/** The Logs screen's Case line: the best bundle's weight and line (the UI shows only the line). */
export function caseStrength(career: Career, data: CaseData, ctx: CaseContext = caseContextOf(career)): CaseStrength {
  const bundle = bestBundle(career, data)
  const weight = evidenceWeight(career, bundle, data, ctx)
  return { weight, line: caseLineOf(weight, data.case), bundle }
}

// ---------------------------------------------------------------------------------------------
// Repeatable corroboration (spec §2.5)
// ---------------------------------------------------------------------------------------------

export type CorroborationRefusal = 'meterOnly' | 'maxReached' | 'tooSoon' | 'insufficientFunds'

/** Why `link` can't be corroborated now, or null when it can. */
export function corroborationRefusal(career: Career, link: CaseLink, cfg: CaseConfig): CorroborationRefusal | null {
  if (link === 'knowledge') return 'meterOnly'
  const taken = (storyOf(career).corroborations ?? []).filter((c) => c.link === link)
  if (taken.length >= cfg.corroboration.maxPerLink) return 'maxReached'
  const now = onlineDaysOf(career)
  const last = taken[taken.length - 1]
  if (last !== undefined && now - last.onlineDay < cfg.corroboration.everyOnlineDays) return 'tooSoon'
  if (career.wallet < cfg.corroboration.fees[link]) return 'insufficientFunds'
  return null
}

/**
 * Takes one repeatable corroboration for `link` (the archivist's insurance pull, Kell's transfer-order contract
 * reward, Duarte's certified extract or the archivist's registry copy): pays the fee and records the instance,
 * which `availableEntries` then offers as `<template>#<n>`.
 */
export function takeCorroboration(
  career: Career,
  link: CaseLink,
  cfg: CaseConfig,
): { ok: true; career: Career } | { ok: false; reason: CorroborationRefusal } {
  const refusal = corroborationRefusal(career, link, cfg)
  if (refusal !== null) return { ok: false, reason: refusal }
  const fee = cfg.corroboration.fees[link]
  const paid = fee > 0 ? withWallet(career, -fee) : career
  const onlineDay = onlineDaysOf(career)
  const next = withStory(paid, (s) => ({ ...s, corroborations: [...(s.corroborations ?? []), { link, onlineDay }] }))
  return { ok: true, career: next }
}
